import { useEffect, useState, type JSX } from "react";
import { App } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CRM_URL } from "../design/AccountMenu";
import { feedbackApi, isCrmAuthError } from "./api";
import { IconExternalLink, IconPaperclip, IconSend } from "./icons";
import {
  ago,
  authorStatusLabel,
  eventWhat,
  fmtDT,
  statusOf,
  type FeedbackEvent,
  type MyTicket,
} from "./model";
import { authorQuestionMeta } from "./supportFormat";
import { KindIcon, Pill, StatusPill } from "./ui";

/** «Мои обращения» (не партнёр): статусы и ответы команды. Партнёру их показывает кабинет партнёра в CRM. */
export function MineList({
  onOpen,
  onNew,
  onAuthLost,
}: {
  onOpen: (id: string) => void;
  onNew: (k: "bug" | "idea") => void;
  onAuthLost: () => void;
}): JSX.Element {
  const q = useQuery({
    queryKey: ["feedback", "my"],
    queryFn: feedbackApi.my,
    staleTime: 15_000,
    retry: false,
  });
  useEffect(() => {
    if (isCrmAuthError(q.error)) onAuthLost();
  }, [q.error, onAuthLost]);
  const rows: MyTicket[] = q.data ?? [];
  const authError = isCrmAuthError(q.error);
  return (
    <div className="fk-fb-pad">
      {q.isLoading ? <div className="fk-fb-empty">Загружаем…</div> : null}
      {q.isError && !authError ? (
        <div className="fk-fb-empty">Не удалось загрузить обращения. Попробуйте позже.</div>
      ) : null}
      {!q.isLoading && !q.isError && !rows.length ? (
        <div className="fk-fb-empty">
          Обращений пока нет. Нашли ошибку или есть идея — расскажите!
        </div>
      ) : null}
      {rows.map((r) => {
        // Вопрос в УК: отдел, кто взял, до какого часа ответ.
        const qm = authorQuestionMeta(r);
        return (
          <button type="button" key={r.id} className="fk-fb-item" onClick={() => onOpen(r.id)}>
            <KindIcon kind={r.kind} />
            <span className="fk-fb-item-main">
              <span className="fk-fb-item-title">{r.title}</span>
              <span className="fk-fb-item-meta">
                <span className="fk-mono">{r.code}</span>
                <span>{ago(r.updatedAt)}</span>
                {!r.isMine && r.authorName ? <span>· {r.authorName}</span> : null}
                {qm.parts.map((p) => (
                  <span key={p}>· {p}</span>
                ))}
                {qm.urgent ? <span className="fk-fb-urgent">· срочно</span> : null}
              </span>
            </span>
            {r.authorUnread ? <span className="fk-fb-unread" title="есть новый ответ" /> : null}
            <StatusPill status={r.status} label={authorStatusLabel(r.status)} />
          </button>
        );
      })}
      <div className="fk-fb-new">
        <button type="button" className="fk-fb-new-btn" onClick={() => onNew("bug")}>
          + Сообщить об ошибке
        </button>
        <button type="button" className="fk-fb-new-btn" onClick={() => onNew("idea")}>
          + Предложить идею
        </button>
      </div>
    </div>
  );
}

function Timeline({
  events,
  ticketKind,
}: {
  events: FeedbackEvent[];
  ticketKind: MyTicket["kind"];
}): JSX.Element {
  return (
    <div className="fk-fb-tl">
      {events.map((ev) => {
        const tone =
          ev.type === "status"
            ? statusOf(ev.toStatus ?? "").tone
            : ev.type === "reply"
              ? "var(--fk-orange)"
              : ev.type === "verify_ok"
                ? "var(--fk-success-text)"
                : ev.type === "verify_fail"
                  ? "var(--fk-error)"
                  : "var(--fk-border-strong)";
        return (
          <div key={ev.id} className="fk-fb-tl-item">
            <span className="fk-fb-tl-dot" style={{ background: tone }} />
            <div className="fk-fb-tl-head">
              <b>{ev.type === "system" ? "CRM" : (ev.authorName ?? "Система")}</b>
              <span>{eventWhat(ev, ticketKind)}</span>
              {ev.internal ? <Pill tone="var(--fk-warning-text)">для команды</Pill> : null}
              <span className="fk-fb-tl-date">{fmtDT(ev.createdAt)}</span>
            </div>
            {ev.text ? (
              <div className="fk-fb-bubble" data-reply={ev.type === "reply" ? "true" : "false"}>
                {ev.type === "status" && ev.toStatus === "rejected" ? <b>Причина: </b> : null}
                {ev.text}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** Карточка обращения: лента, вложения (названия) и поле «Дополнить или ответить команде». */
export function MineDetail({
  id,
  full,
  onChanged,
  onAuthLost,
}: {
  id: string;
  full: boolean;
  onChanged: () => void;
  onAuthLost: () => void;
}): JSX.Element {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["feedback", "my", id],
    queryFn: () => feedbackApi.myOne(id),
    retry: false,
  });
  const [text, setText] = useState("");
  const comment = useMutation({
    mutationFn: () => feedbackApi.comment(id, text.trim()),
    onSuccess: () => {
      setText("");
      message.success("Комментарий отправлен команде");
      void qc.invalidateQueries({ queryKey: ["feedback"] });
      onChanged();
    },
    onError: (e: Error) => {
      if (isCrmAuthError(e)) onAuthLost();
      else message.error(e.message);
    },
  });

  useEffect(() => {
    if (isCrmAuthError(q.error)) onAuthLost();
  }, [q.error, onAuthLost]);

  // Отметка «прочитано» ставится сервером при открытии — счётчик на кнопке обновим.
  useEffect(() => {
    if (q.data) onChanged();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data?.id]);

  if (q.isLoading) return <div className="fk-fb-empty">Загружаем…</div>;
  if (!q.data) {
    return (
      <div className="fk-fb-empty">
        {isCrmAuthError(q.error) ? "Нужен вход в CRM" : "Обращение не найдено"}
      </div>
    );
  }
  const t = q.data;
  const qm = authorQuestionMeta(t);
  return (
    <div className="fk-fb-pad fk-fb-detail">
      <div>
        <div className="fk-fb-detail-top">
          <span className="fk-mono fk-fb-muted">{t.code}</span>
          <StatusPill status={t.status} label={authorStatusLabel(t.status)} />
        </div>
        <div className="fk-fb-detail-title">{t.title}</div>
        {t.status === "rejected" && t.rejectReason ? (
          <div className="fk-fb-sub">Причина: {t.rejectReason}</div>
        ) : null}
        {qm.parts.length || qm.urgent ? (
          <div className="fk-fb-sub">
            {qm.parts.join(" · ")}
            {qm.urgent ? (
              <span className="fk-fb-urgent">{qm.parts.length ? " · " : ""}срочно</span>
            ) : null}
          </div>
        ) : null}
      </div>

      {t.status === "verify" ? (
        <div className="fk-fb-verify">
          <b>Мы исправили — проверьте, пожалуйста</b>
          <span>Подтвердить, что всё работает, можно в CRM.</span>
          <a
            className="fk-fb-link"
            href={`${CRM_URL}profile?feedback=${encodeURIComponent(t.id)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Открыть в CRM <IconExternalLink size={12} />
          </a>
        </div>
      ) : null}

      <div className="fk-fb-cols" data-full={full ? "true" : "false"}>
        <div>
          {t.expected ? (
            <div className="fk-fb-sub fk-fb-gap-b">
              <b>Ожидали:</b> {t.expected}
            </div>
          ) : null}
          <Timeline events={t.events} ticketKind={t.kind} />
        </div>
        <div>
          <textarea
            className="fk-fb-input fk-fb-textarea fk-fb-textarea--sm"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Дополнить или ответить команде… (Ctrl+Enter)"
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && text.trim() && !comment.isPending)
                comment.mutate();
            }}
            maxLength={3000}
          />
          <div className="fk-fb-right">
            <button
              type="button"
              className="fk-fb-send fk-fb-send--sm"
              disabled={!text.trim() || comment.isPending}
              onClick={() => comment.mutate()}
            >
              <IconSend size={13} />
              Отправить
            </button>
          </div>
          {t.attachments.length ? (
            <div className="fk-fb-gap">
              <div className="fk-fb-label">Вложения</div>
              {t.attachments.map((a) => (
                <div key={a.id} className="fk-fb-attach">
                  <IconPaperclip size={13} />
                  <span>{a.filename}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
